import './App.css';
import { useState, useEffect } from "react";
import { ConfigProvider, Button, Switch, DatePicker, Select, Checkbox, Radio, Modal, Pagination, Spin, Input } from "antd";
import { LogoutOutlined, CalendarOutlined, SearchOutlined, FilterOutlined, ApiOutlined, CloudServerOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { FINDING_STREAM_SERVER } from "./config";
import enUS from 'antd/locale/en_US';
import dayjs from "dayjs";
import axios from "axios";
import moment from "moment-timezone";
import _ from "lodash";
import Login from "./Login";
import MatchInfo from "./MatchInfo";
import { STREAM_SERVER } from "./config";

const CheckboxGroup = Checkbox.Group;

function App() {
    // Auth state
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return localStorage.getItem('bdtv_auth') === 'true';
    });

    // Match data state
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [referer, setReferer] = useState('');
    const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYYMMDD"));
    const [showFeatured, setShowFeatured] = useState(true);
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(() => {
        return localStorage.getItem('bdtv_selected_provider') || 'XT';
    });

    // Filter state
    const [filteredMatches, setFilteredMatches] = useState([]);
    const [filteredOptions, setFilteredOptions] = useState({});
    const [searchText, setSearchText] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 24;

    // Modal state
    const [selectedMatch, setSelectedMatch] = useState(null);

    const handleLogout = () => {
        localStorage.removeItem('bdtv_auth');
        setIsAuthenticated(false);
    };

    // Fetch settings
    const getSettings = async () => {
        try {
            const response = await axios.get(`${STREAM_SERVER}/settings`);
            setReferer(response.data.referer);
        } catch (error) {
            console.error('Failed to fetch settings:', error);
        }
    };

    // Fetch providers
    const getProviders = async () => {
        try {
            const response = await axios.get(`${FINDING_STREAM_SERVER}/tc/providers/`);
            setProviders(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch providers:', error);
        }
    };

    // Fetch matches
    const getFeaturedMatches = async (provider) => {
        setLoading(true);
        try {
            const response = await axios.get(`${STREAM_SERVER}/vebo/match/featured?provider=${provider || selectedProvider}`);
            setMatches(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch matches:', error);
            setMatches([]);
        } finally {
            setLoading(false);
        }
    };

    const getAllMatches = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${STREAM_SERVER}/vebo/match/${selectedDate}`);
            setMatches(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch matches:', error);
            setMatches([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            getSettings();
            getProviders();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            showFeatured ? getFeaturedMatches(selectedProvider) : getAllMatches();
        }
    }, [selectedDate, showFeatured, isAuthenticated, selectedProvider]);

    // Filter logic
    const footballMatches = matches?.filter(m => m.sport_type === "football") || [];
    const tournamentGroups = _.groupBy(footballMatches, value => value.tournament?.name);
    const tournamentFilters = Object.keys(tournamentGroups).map(name => ({
        label: `${name} (${tournamentGroups[name]?.length})`,
        value: name
    }));

    const matchByStatus = _.groupBy(footballMatches, value => value.match_status);
    const statusFilters = Object.keys(matchByStatus).map(text => ({
        label: `${text} (${matchByStatus[text]?.length})`,
        value: text
    }));

    useEffect(() => {
        const { status, tournaments, sorter } = filteredOptions;
        let filtered = footballMatches;

        // Fuzzy search
        if (searchText.trim()) {
            const search = searchText.toLowerCase().trim();
            filtered = filtered.filter(m => {
                const homeName = m.home?.name?.toLowerCase() || '';
                const awayName = m.away?.name?.toLowerCase() || '';
                const tournamentName = m.tournament?.name?.toLowerCase() || '';
                const commentators = m.commentators?.map(c => c.name?.toLowerCase()).join(' ') || '';

                return homeName.includes(search) ||
                       awayName.includes(search) ||
                       tournamentName.includes(search) ||
                       commentators.includes(search);
            });
        }

        if (status?.length > 0) {
            filtered = filtered.filter(t => status.includes(t.match_status));
        }
        if (tournaments?.length > 0) {
            filtered = filtered.filter(t => tournaments.includes(t.tournament?.name));
        }
        if (sorter) {
            const [field, direction] = sorter.split("-");
            filtered = _.orderBy(filtered, field, direction);
        }
        setFilteredMatches(filtered);
        setCurrentPage(1);
    }, [filteredOptions, matches, searchText]);

    // Paginated matches
    const paginatedMatches = filteredMatches.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    if (!isAuthenticated) {
        return <Login onLoginSuccess={() => setIsAuthenticated(true)} />;
    }

    return (
        <ConfigProvider locale={enUS} theme={{
            token: {
                colorPrimary: '#3b82f6',
                borderRadius: 8,
            },
        }}>
            <div className="bdtv-container">
                {/* Header */}
                <header className="bdtv-header">
                    <div className="bdtv-logo">
                        <img className="bdtv-icon" src="football_512.png" alt="" />
                        <img
                            className="bdtv-text-logo"
                            src="https://see.fontimg.com/api/renderfont4/3z8d8/eyJyIjoiZnMiLCJoIjoxMzAsInciOjIwMDAsImZzIjo2NSwiZmdjIjoiI0ZGRkZGRiIsImJnYyI6IiNGRkZGRkYiLCJ0IjoxfQ/QkRUVg/rooster-personal-use.png"
                            alt="BDTV"
                        />
                    </div>
                    <div className="bdtv-controls">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8' }}>
                            <span style={{ fontSize: 13 }}>Featured</span>
                            <Switch
                                checked={showFeatured}
                                onChange={setShowFeatured}
                                size="small"
                            />
                        </div>
                        {!showFeatured && (
                            <DatePicker
                                onChange={(date) => setSelectedDate(date.format("YYYYMMDD"))}
                                defaultValue={dayjs()}
                                size="small"
                                suffixIcon={<CalendarOutlined style={{ color: '#64748b' }} />}
                            />
                        )}
                        <Link to="/status">
                            <Button
                                type="text"
                                icon={<ApiOutlined />}
                                style={{ color: '#94a3b8' }}
                                title="Provider Status"
                            />
                        </Link>
                        <Button
                            type="text"
                            icon={<LogoutOutlined />}
                            onClick={handleLogout}
                            style={{ color: '#94a3b8' }}
                        />
                    </div>
                </header>

                {/* Content */}
                <div className="bdtv-content">
                    {/* Filters */}
                    <div className="bdtv-filters">
                        <div className="filter-row">
                            <div className="filter-item search-item">
                                <Input
                                    placeholder="Search team, tournament..."
                                    prefix={<SearchOutlined style={{ color: '#64748b' }} />}
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    allowClear
                                    style={{ width: 200 }}
                                />
                            </div>
                            {showFeatured && (
                                <div className="filter-item">
                                    <CloudServerOutlined style={{ color: '#64748b' }} />
                                    <Select
                                        value={selectedProvider}
                                        onChange={(code) => {
                                            setSelectedProvider(code);
                                            localStorage.setItem('bdtv_selected_provider', code);
                                        }}
                                        options={providers.map(p => ({ label: p.name, value: p.code }))}
                                        style={{ width: 130 }}
                                        popupMatchSelectWidth={150}
                                    />
                                </div>
                            )}
                            <div className="filter-item filter-advanced">
                                <span className="filter-label">Tournaments:</span>
                                <Select
                                    value={filteredOptions.tournaments}
                                    onChange={value => setFilteredOptions({ ...filteredOptions, tournaments: value })}
                                    options={tournamentFilters}
                                    allowClear
                                    mode="multiple"
                                    placeholder="All"
                                    style={{ minWidth: 160 }}
                                    popupMatchSelectWidth={300}
                                />
                            </div>
                            <div className="filter-item filter-advanced">
                                <span className="filter-label">Status:</span>
                                <CheckboxGroup
                                    options={statusFilters}
                                    value={filteredOptions.status}
                                    onChange={checkedValue => setFilteredOptions({ ...filteredOptions, status: checkedValue })}
                                />
                            </div>
                            <div className="filter-item filter-advanced" style={{ marginLeft: 'auto' }}>
                                <Radio.Group
                                    defaultValue="timestamp-asc"
                                    onChange={val => setFilteredOptions({ ...filteredOptions, sorter: val.target.value })}
                                    size="small"
                                >
                                    <Radio.Button value="timestamp-desc">Latest</Radio.Button>
                                    <Radio.Button value="timestamp-asc">Earliest</Radio.Button>
                                </Radio.Group>
                            </div>
                            <Button
                                className="filter-toggle-btn"
                                type={showFilters ? "primary" : "default"}
                                icon={<FilterOutlined />}
                                onClick={() => setShowFilters(!showFilters)}
                                size="small"
                            >
                                Filters
                            </Button>
                        </div>
                        <div className={`filter-row filter-row-mobile ${showFilters ? 'show' : ''}`}>
                            <div className="filter-item">
                                <span className="filter-label">Tournaments:</span>
                                <Select
                                    value={filteredOptions.tournaments}
                                    onChange={value => setFilteredOptions({ ...filteredOptions, tournaments: value })}
                                    options={tournamentFilters}
                                    allowClear
                                    mode="multiple"
                                    placeholder="All tournaments"
                                    style={{ flex: 1 }}
                                    popupMatchSelectWidth={300}
                                />
                            </div>
                            <div className="filter-item">
                                <span className="filter-label">Status:</span>
                                <CheckboxGroup
                                    options={statusFilters}
                                    value={filteredOptions.status}
                                    onChange={checkedValue => setFilteredOptions({ ...filteredOptions, status: checkedValue })}
                                />
                            </div>
                            <div className="filter-item">
                                <Radio.Group
                                    defaultValue="timestamp-asc"
                                    onChange={val => setFilteredOptions({ ...filteredOptions, sorter: val.target.value })}
                                    size="small"
                                >
                                    <Radio.Button value="timestamp-desc">Latest</Radio.Button>
                                    <Radio.Button value="timestamp-asc">Earliest</Radio.Button>
                                </Radio.Group>
                            </div>
                        </div>
                    </div>

                    {/* Match Grid */}
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <>
                            <div className="bdtv-matches">
                                {paginatedMatches.map((match) => (
                                    <MatchCard
                                        key={match.id}
                                        match={match}
                                        onClick={() => setSelectedMatch({
                                            matchId: match.id,
                                            urls: match.play_urls,
                                            linkId: match.linkId,
                                            slug: match.slug
                                        })}
                                    />
                                ))}
                            </div>

                            {filteredMatches.length === 0 && !loading && (
                                <div className="empty-state">
                                    <div className="empty-state-icon">⚽</div>
                                    <div>No matches found</div>
                                </div>
                            )}

                            {filteredMatches.length > pageSize && (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                                    <Pagination
                                        current={currentPage}
                                        total={filteredMatches.length}
                                        pageSize={pageSize}
                                        onChange={setCurrentPage}
                                        showSizeChanger={false}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Match Info Modal */}
                <Modal
                    title="Stream Links"
                    open={!!selectedMatch}
                    onCancel={() => setSelectedMatch(null)}
                    footer={null}
                    width={500}
                >
                    <MatchInfo {...{ ...(selectedMatch || {}), referer, isVebo: true }} />
                </Modal>
            </div>
        </ConfigProvider>
    );
}

// Match Card Component
function MatchCard({ match, onClick }) {
    const time = moment(match.timestamp).tz('Asia/Jakarta');
    const isLive = match.match_status === "live";
    const isFinished = match.match_status === "finished";
    const isPlaying = isLive || isFinished;

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
    };

    return (
        <div
            className={`match-card ${isLive ? 'is-live' : ''}`}
            onClick={onClick}
        >
            {/* Tournament */}
            <div className="match-tournament">
                {match.tournament?.logo ? (
                    <img src={match.tournament.logo} alt="" />
                ) : (
                    <div className="tournament-logo-placeholder" />
                )}
                <span>{match.tournament?.name || 'Unknown Tournament'}</span>
                {isLive && (
                    <span className="live-badge" style={{ marginLeft: 'auto' }}>LIVE</span>
                )}
            </div>

            {/* Teams */}
            <div className="match-teams">
                <div className="match-team">
                    {match.home?.logo ? (
                        <img src={match.home.logo} alt="" />
                    ) : (
                        <div className="team-logo-placeholder">{getInitials(match.home?.name)}</div>
                    )}
                    <span className="team-name">{match.home?.name || 'TBD'}</span>
                    {isPlaying && match.scores && (
                        <span className="team-score">{match.scores.home}</span>
                    )}
                </div>

                <div className="match-vs">
                    <span className="match-time">{time.format('HH:mm')}</span>
                    <span className="match-date">{time.format('DD/MM')}</span>
                </div>

                <div className="match-team">
                    {match.away?.logo ? (
                        <img src={match.away.logo} alt="" />
                    ) : (
                        <div className="team-logo-placeholder">{getInitials(match.away?.name)}</div>
                    )}
                    <span className="team-name">{match.away?.name || 'TBD'}</span>
                    {isPlaying && match.scores && (
                        <span className="team-score">{match.scores.away}</span>
                    )}
                </div>
            </div>

            {/* Commentators */}
            {match.commentators?.length > 0 && (
                <div className="match-commentators">
                    {match.commentators.map((c, i) => (
                        <span key={i} className="commentator-tag">{c.name}</span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default App;
